import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Wallet, Search, FileText, Download } from "lucide-react";
import { api, API, fmtINR, fmtDate, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canWrite } from "../lib/permissions";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const statusBadge = (s, overdue) => {
  const base = "rounded-sm capitalize";
  if (overdue > 0) return <Badge variant="outline" className={`${base} bg-red-100 text-red-700 border-red-200`}>{s} · {overdue}d overdue</Badge>;
  if (String(s).toLowerCase() === "partial") return <Badge variant="outline" className={`${base} bg-amber-100 text-amber-700 border-amber-200`}>{s}</Badge>;
  return <Badge variant="outline" className={`${base} bg-slate-100 text-slate-700 border-slate-200`}>{s}</Badge>;
};

export default function DuesPage() {
  const { user } = useAuth();
  const canAllocate = canWrite(user, "payments");
  const [tab, setTab] = useState("receivables");
  const [rec, setRec] = useState([]);
  const [pay, setPay] = useState([]);
  const [exp, setExp] = useState([]);
  const [jw, setJw] = useState({ rows: [], total_outstanding: 0, by_vendor: [] });
  const [jwPay, setJwPay] = useState(null);
  const [mm, setMm] = useState(null);
  const [fixing, setFixing] = useState(false);
  const [jwForm, setJwForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [modal, setModal] = useState(null); // {row, type}
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([api.get("/dues/receivables"), api.get("/dues/payables")]);
      setRec(r.data); setPay(p.data);
      api.get("/expenses").then((e) => setExp(e.data)).catch(() => {});
      api.get("/dues/vendor-expenses").then((j) => setJw(j.data)).catch(() => {});
      api.get("/payments/mismatch").then((m) => setMm(m.data)).catch(() => {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openPay = (row) => {
    setForm({ amount: row.outstanding, payment_date: new Date().toISOString().slice(0, 10), payment_mode: "Bank Transfer", reference_number: "" });
    setModal({ row, type: tab === "receivables" ? "customer" : "supplier" });
  };
  const submitPay = async () => {
    if (!(Number(form.amount) > 0)) { toast.error("Enter a valid amount"); return; }
    try {
      const res = await api.post("/payments/allocate", {
        party_type: modal.type, doc_id: modal.row.doc_id, amount: Number(form.amount),
        payment_date: form.payment_date, payment_mode: form.payment_mode,
        reference_number: form.reference_number, remarks: form.remarks,
      });
      const isCust = modal.type === "customer";
      setModal(null); setAging(null); load();
      if (isCust && res.data?.id) {
        const url = `${API}/pdf/receipt/${res.data.id}?token=${localStorage.getItem("token")}`;
        toast.success("Payment recorded", { action: { label: "Download Receipt", onClick: () => window.open(url, "_blank") } });
      } else {
        toast.success("Payment allocated");
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openJwPay = (r) => {
    setJwForm({ amount: r.outstanding, payment_date: new Date().toISOString().slice(0, 10), payment_mode: "Cash", reference_number: "", bill_no: r.bill_no || "" });
    setJwPay(r);
  };
  const submitJwPay = async () => {
    if (!(Number(jwForm.amount) > 0)) { toast.error("Enter a valid amount"); return; }
    try {
      const { data } = await api.post("/dues/expense/pay", {
        source: jwPay.source, record_id: jwPay.record_id, expense_index: jwPay.expense_index, amount: Number(jwForm.amount),
        payment_date: jwForm.payment_date, payment_mode: jwForm.payment_mode,
        reference_number: jwForm.reference_number, bill_no: jwForm.bill_no,
      });
      setJwPay(null); load();
      if (data.whatsapp_link) toast.success(`Paid ${fmtINR(data.paid_now)} · ${data.expense_status}`, { action: { label: "WhatsApp Vendor", onClick: () => window.open(data.whatsapp_link, "_blank") } });
      else toast.success(`Paid ${fmtINR(data.paid_now)} · ${data.expense_status}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const reconcile = async () => {
    setFixing(true);
    try {
      const { data } = await api.post("/payments/reconcile");
      toast.success(`${data.bills_corrected} bill(s) corrected · ${data.payments_applied} payment(s) applied${data.amount_applied ? ` (${fmtINR(data.amount_applied)})` : ""}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setFixing(false);
  };

  const [aging, setAging] = useState(null);
  useEffect(() => { if (tab === "aging" && !aging) api.get("/reports/receivables-aging").then((r) => setAging(r.data)).catch(() => {}); }, [tab, aging]);

  const rows = tab === "receivables" ? rec : pay;
  const filtered = useMemo(() => {
    if (!query) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) => [r.party, r.doc_no, r.status].some((v) => String(v || "").toLowerCase().includes(q)));
  }, [rows, query]);
  const totalOut = useMemo(() => filtered.reduce((s, r) => s + r.outstanding, 0), [filtered]);

  const Table = ({ list }) => (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto mt-4">
      <table className="w-full text-sm">
        <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">
          {[tab === "receivables" ? "Customer" : "Supplier", tab === "receivables" ? "Invoice" : "Bill", "Date", "Amount", "Paid", "Outstanding", "Due Date", "Status", canAllocate ? "Action" : ""].map((h, i) => (
            <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap ${h === "Action" ? "text-right" : ""}`}>{h}</th>))}
        </tr></thead>
        <tbody>
          {loading ? <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Loading...</td></tr>
            : list.length === 0 ? <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No outstanding dues</td></tr>
            : list.map((r) => (
              <tr key={r.doc_id} data-testid={`dues-row-${r.doc_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-800">{r.party || "-"}</td>
                <td className="px-4 py-3 text-slate-700">{r.doc_no}</td>
                <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtDate(r.doc_date)}</td>
                <td className="px-4 py-3 font-mono tabular-nums">{fmtINR(r.amount)}</td>
                <td className="px-4 py-3 font-mono tabular-nums text-emerald-600">{fmtINR(r.paid)}</td>
                <td className="px-4 py-3 font-mono tabular-nums text-red-600 font-semibold">{fmtINR(r.outstanding)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-slate-600">{r.due_date ? fmtDate(r.due_date) : "-"}</td>
                <td className="px-4 py-3">{statusBadge(r.status, r.days_overdue)}</td>
                {canAllocate && <td className="px-4 py-3 text-right"><Button data-testid={`record-payment-${r.doc_id}`} size="sm" variant="outline" className="rounded-sm" onClick={() => openPay(r)}>Record Payment</Button></td>}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div data-testid="module-dues">
      <div className="mb-5 flex items-center gap-2"><Wallet className="w-6 h-6 text-[#0B132B]" /><div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">Dues &amp; Payments</h1>
        <p className="text-sm text-slate-500 mt-0.5">Receivables &amp; payables · allocate payments to invoices / bills</p></div></div>

      {mm?.needs_fix > 0 && (
        <div data-testid="payment-mismatch-banner" className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3">
          <div className="text-sm text-amber-900">
            <b>Payments not matched to bills.</b>{" "}
            {mm.drift_count > 0 && <span data-testid="mismatch-drift-count">{mm.drift_count} bill(s) show a wrong paid amount. </span>}
            {mm.unapplied_count > 0 && <span data-testid="mismatch-unapplied">{fmtINR(mm.unapplied_total)} across {mm.unapplied_count} payment(s) is recorded in the company balance but not applied to any bill. </span>}
            Fix it so paid bills leave this list.
          </div>
          {canAllocate && <Button data-testid="payment-reconcile-btn" disabled={fixing} onClick={reconcile} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm shrink-0">{fixing ? "Fixing..." : "Fix Now"}</Button>}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <TabsList>
            <TabsTrigger value="receivables" data-testid="tab-receivables">Receivables ({rec.length})</TabsTrigger>
            <TabsTrigger value="payables" data-testid="tab-payables">Payables ({pay.length})</TabsTrigger>
            <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses ({exp.length})</TabsTrigger>
            <TabsTrigger value="jobwork" data-testid="tab-jobwork">Vendor Expenses ({jw.rows.length})</TabsTrigger>
            <TabsTrigger value="aging" data-testid="tab-aging">Aging</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-3">
            {tab === "aging" && (
              <div className="flex items-center gap-2">
                <Button data-testid="aging-export-pdf" size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => window.open(`${API}/pdf/aging?token=${localStorage.getItem("token")}`, "_blank")}><FileText className="w-4 h-4" /> PDF</Button>
                <Button data-testid="aging-export-excel" size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => window.open(`${API}/reports/receivables-aging/xlsx?token=${localStorage.getItem("token")}`, "_blank")}><Download className="w-4 h-4" /> Excel</Button>
              </div>
            )}
            <div className="text-sm text-slate-500">Total Outstanding: <span className="font-mono font-semibold text-red-600">{fmtINR(totalOut)}</span></div>
            <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="search-dues" placeholder="Search..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 w-44 bg-white" /></div>
          </div>
        </div>
        <TabsContent value="receivables"><Table list={filtered} /></TabsContent>
        <TabsContent value="payables"><Table list={filtered} /></TabsContent>
        <TabsContent value="expenses">
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto mt-4" data-testid="expenses-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">
                {["Voucher", "Date", "Category", "Paid To", "Description", "Amount"].map((h, i) => (
                  <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap ${h === "Amount" ? "text-right" : ""}`}>{h}</th>))}
              </tr></thead>
              <tbody>
                {exp.length === 0 ? <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No expenses recorded</td></tr>
                  : exp.map((e) => (
                    <tr key={e.id} data-testid={`expense-row-${e.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-700">{e.voucher_no || "-"}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtDate(e.expense_date)}</td>
                      <td className="px-4 py-3 text-slate-700">{e.category || "-"}</td>
                      <td className="px-4 py-3 text-slate-700">{e.paid_to || "-"}</td>
                      <td className="px-4 py-3 text-slate-600">{e.description || e.remarks || "-"}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-rose-600 font-semibold">{fmtINR(e.amount)}</td>
                    </tr>))}
                <tr className="bg-slate-50 font-semibold border-t-2 border-slate-200">
                  <td className="px-4 py-3" colSpan={5}>TOTAL EXPENSES PAID</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-rose-600" data-testid="expenses-total">{fmtINR(exp.reduce((sm, e) => sm + Number(e.amount || 0), 0))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="jobwork">
          <div className="mt-4 space-y-4" data-testid="jobwork-dues">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Vendor Expense Payable</div>
                <div className="text-lg font-mono font-semibold text-red-600 mt-1.5" data-testid="jobwork-dues-total">{fmtINR(jw.total_outstanding)}</div>
                <div className="text-xs text-slate-400 mt-1">{jw.rows.length} pending charge(s) · Job Work, Purchase &amp; Sales bills</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-4 sm:col-span-2">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Vendor-wise pending</div>
                {!jw.by_vendor.length ? <div className="text-sm text-slate-400">Nothing pending</div>
                  : <div className="space-y-1 max-h-24 overflow-y-auto">
                    {jw.by_vendor.map((v, i) => (
                      <div key={i} data-testid={`jobwork-vendor-due-${i}`} className="flex justify-between text-sm">
                        <span className="text-slate-700 truncate">{v.vendor_name} <span className="text-slate-400">· {v.bills} bill(s)</span></span>
                        <span className="font-mono tabular-nums text-red-600">{fmtINR(v.outstanding)}</span>
                      </div>))}
                  </div>}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">
                  {["Vendor", "Source", "Document", "Charge", "Bill No", "Date", "Amount", "Paid", "Outstanding", "Pending", "Status", canAllocate ? "Action" : ""].map((h, i) => (
                    <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap ${h === "Action" ? "text-right" : ""}`}>{h}</th>))}
                </tr></thead>
                <tbody>
                  {!jw.rows.length ? <tr><td colSpan={12} className="px-4 py-10 text-center text-slate-400" data-testid="jobwork-dues-empty">No vendor expense amount pending</td></tr>
                    : jw.rows.filter((r) => !query || [r.vendor_name, r.doc_no, r.category, r.bill_no, r.source_label].some((v) => String(v || "").toLowerCase().includes(query.toLowerCase()))).map((r) => (
                      <tr key={r.doc_id} data-testid={`jobwork-dues-row-${r.doc_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-800">{r.vendor_name}</td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{r.source_label}</td>
                        <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{r.doc_no}<div className="text-[10px] text-slate-400">{r.against}</div></td>
                        <td className="px-4 py-3 text-slate-700">{r.category}{r.description ? <div className="text-[10px] text-slate-400">{r.description}</div> : null}</td>
                        <td className="px-4 py-3 text-slate-600">{r.bill_no || "-"}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtDate(r.date)}</td>
                        <td className="px-4 py-3 font-mono tabular-nums">{fmtINR(r.amount)}</td>
                        <td className="px-4 py-3 font-mono tabular-nums text-emerald-600">{fmtINR(r.paid)}</td>
                        <td className="px-4 py-3 font-mono tabular-nums text-red-600 font-semibold">{fmtINR(r.outstanding)}</td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{r.days_pending ? `${r.days_pending}d` : "-"}</td>
                        <td className="px-4 py-3">{statusBadge(r.status, 0)}</td>
                        {canAllocate && <td className="px-4 py-3 text-right"><Button data-testid={`jobwork-pay-${r.doc_id}`} size="sm" variant="outline" className="rounded-sm" onClick={() => openJwPay(r)}>Pay</Button></td>}
                      </tr>))}
                  {jw.rows.length > 0 && (
                    <tr className="bg-slate-50 font-semibold border-t-2 border-slate-200">
                      <td className="px-4 py-3" colSpan={8}>TOTAL VENDOR EXPENSE PAYABLE</td>
                      <td className="px-4 py-3 font-mono tabular-nums text-red-600">{fmtINR(jw.total_outstanding)}</td>
                      <td colSpan={3}></td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="aging">
          <div data-testid="aging-report" className="bg-white border border-slate-200 rounded-sm overflow-x-auto mt-4">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">
                {["Customer", "0–30 Days", "31–60 Days", "61–90 Days", "90+ Days", "Total Outstanding"].map((h, i) => (
                  <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap ${i > 0 ? "text-right" : ""}`}>{h}</th>))}
              </tr></thead>
              <tbody>
                {!aging ? <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Loading...</td></tr>
                  : aging.rows.length === 0 ? <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No outstanding receivables</td></tr>
                  : aging.rows.map((r, i) => (
                    <tr key={i} data-testid={`aging-row-${r.customer_id || i}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800">{r.customer_name}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">{fmtINR(r.b0_30)}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">{fmtINR(r.b31_60)}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-amber-600">{fmtINR(r.b61_90)}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-red-600 font-semibold">{fmtINR(r.b90)}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold">{fmtINR(r.total)}</td>
                    </tr>))}
                {aging && aging.rows.length > 0 && (
                  <tr className="bg-slate-50 font-semibold border-t-2 border-slate-200">
                    <td className="px-4 py-3">TOTAL</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{fmtINR(aging.totals.b0_30)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{fmtINR(aging.totals.b31_60)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{fmtINR(aging.totals.b61_90)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{fmtINR(aging.totals.b90)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-red-600">{fmtINR(aging.totals.total)}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-head">Record Payment</DialogTitle>
            <DialogDescription>{modal?.row?.doc_no} · {modal?.row?.party} · Outstanding {fmtINR(modal?.row?.outstanding)}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Amount (₹)</label>
              <Input data-testid="pay-amount" type="number" min="0" value={form.amount ?? ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-white" /></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Payment Date</label>
              <Input data-testid="pay-date" type="date" value={form.payment_date || ""} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="bg-white" /></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Mode</label>
              <Select value={form.payment_mode || "Bank Transfer"} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                <SelectTrigger data-testid="pay-mode" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{["Cash", "Bank Transfer", "Cheque", "UPI", "NEFT/RTGS"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reference No</label>
              <Input data-testid="pay-ref" value={form.reference_number || ""} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} className="bg-white" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)} className="rounded-sm">Cancel</Button>
            <Button data-testid="pay-submit" onClick={submitPay} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm">Allocate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!jwPay} onOpenChange={(o) => !o && setJwPay(null)}>
        <DialogContent className="max-w-md" data-testid="jobwork-pay-dialog">
          <DialogHeader><DialogTitle className="font-head">Pay Vendor Expense</DialogTitle>
            <DialogDescription>{jwPay?.vendor_name} · {jwPay?.source_label} {jwPay?.doc_no} · {jwPay?.category} · Outstanding {fmtINR(jwPay?.outstanding)}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Amount (₹)</label>
              <Input data-testid="jw-pay-amount" type="number" min="0" value={jwForm.amount ?? ""} onChange={(e) => setJwForm({ ...jwForm, amount: e.target.value })} className="bg-white" /></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Payment Date</label>
              <Input data-testid="jw-pay-date" type="date" value={jwForm.payment_date || ""} onChange={(e) => setJwForm({ ...jwForm, payment_date: e.target.value })} className="bg-white" /></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Paid By</label>
              <Select value={jwForm.payment_mode || "Cash"} onValueChange={(v) => setJwForm({ ...jwForm, payment_mode: v })}>
                <SelectTrigger data-testid="jw-pay-mode" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{["Cash", "Bank", "Cheque", "UPI", "NEFT", "RTGS"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vendor Bill No</label>
                <Input data-testid="jw-pay-bill" value={jwForm.bill_no || ""} onChange={(e) => setJwForm({ ...jwForm, bill_no: e.target.value })} className="bg-white" /></div>
              <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reference / UTR</label>
                <Input data-testid="jw-pay-ref" value={jwForm.reference_number || ""} onChange={(e) => setJwForm({ ...jwForm, reference_number: e.target.value })} className="bg-white" /></div>
            </div>
            <p className="text-xs text-slate-400">This debits the chosen cash / bank account and lets you WhatsApp a "payment done" confirmation to the vendor.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setJwPay(null)} className="rounded-sm">Cancel</Button>
            <Button data-testid="jw-pay-submit" onClick={submitJwPay} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm">Pay</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
